/** One request at a time, paused offscreen/offline, refreshed on return. */
export function startVisiblePolling(
  refresh: () => Promise<void>,
  interval: () => number,
  { immediate = false }: { immediate?: boolean } = {},
): () => void {
  let stopped = false;
  let busy = false;
  let lastStarted = -Infinity;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const visible = () => document.visibilityState !== 'hidden' && navigator.onLine !== false;
  const schedule = () => {
    clearTimeout(timer);
    if (!stopped && visible()) timer = setTimeout(run, interval());
  };
  const run = async () => {
    if (stopped || busy || !visible()) return;
    clearTimeout(timer);
    busy = true;
    lastStarted = Date.now();
    try { await refresh(); }
    catch { /* The caller retains its last good data and owns error presentation. */ }
    finally { busy = false; schedule(); }
  };
  const resume = () => {
    if (!visible()) { clearTimeout(timer); return; }
    if (Date.now() - lastStarted >= 2_000) void run();
    else if (!busy) schedule();
  };
  document.addEventListener('visibilitychange', resume);
  window.addEventListener('focus', resume);
  window.addEventListener('online', resume);
  window.addEventListener('offline', resume);
  window.addEventListener('pageshow', resume);
  if (immediate) void run();
  else schedule();
  return () => {
    stopped = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', resume);
    window.removeEventListener('focus', resume);
    window.removeEventListener('online', resume);
    window.removeEventListener('offline', resume);
    window.removeEventListener('pageshow', resume);
  };
}
